/**
 * GlobalAlertBanner — banner sticky en la parte superior de TODA la app
 * que alerta al operador de asociados esperando aprobación.
 *
 * Diferencias con la versión anterior (banner solo en /dashboard):
 *   - Visible en TODAS las rutas (vive en AppShell, no en DashboardPage).
 *   - Animación pulse sutil para llamar la atención sin agotar.
 *   - Botón opt-in para activar Notifications nativas del SO.
 *   - Click en el banner ruta directo a /neocore/asociados?estado=pendiente.
 *   - Si count === 0, el banner no se renderiza (no ocupa espacio).
 *
 * El componente solo lee de useProspectosPendientes — el hook ya maneja
 * el realtime + chime + document title.
 */
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BellRinging,
  SpeakerHigh,
  X,
  Tray as Inbox,
} from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { useProspectosPendientes } from '@/lib/hooks/useProspectosPendientes'

const DISMISS_KEY = 'neocoop-admin-banner-dismissed-at'
const DISMISS_TTL_MS = 5 * 60_000 // 5 minutos — el dismiss es temporal

export function GlobalAlertBanner() {
  const {
    count,
    pendienteCount,
    prospectoCount,
    loading,
    requestNotificationPermission,
    notificationPermission,
    testChime,
  } = useProspectosPendientes()

  // Dismiss temporal — el operador puede cerrar el banner por 5 min
  // pero vuelve a aparecer (no es un dismiss permanente, no podemos
  // dejar al operador sin alerta).
  const [dismissed, setDismissed] = useState(() => {
    try {
      const at = localStorage.getItem(DISMISS_KEY)
      if (!at) return false
      const dismissedAt = parseInt(at, 10)
      if (Number.isNaN(dismissedAt)) return false
      return Date.now() - dismissedAt < DISMISS_TTL_MS
    } catch {
      return false
    }
  })

  // Re-armado automático del banner después del TTL
  useEffect(() => {
    if (!dismissed) return
    const t = setTimeout(() => setDismissed(false), DISMISS_TTL_MS)
    return () => clearTimeout(t)
  }, [dismissed])

  const onDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      // ignore
    }
    setDismissed(true)
  }

  const onActivateNotifications = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    await requestNotificationPermission()
  }

  const onTestChime = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    testChime()
  }

  if (loading || count === 0 || dismissed) return null

  const supportsNotifs = notificationPermission !== 'unsupported'
  const showActivate = supportsNotifs && notificationPermission === 'default'

  return (
    <>
      <style>{bannerStyles}</style>
      <div className="gab-wrap">
        <Link
          to="/neocore/asociados?tab=nuevos"
          className="gab-link"
        >
          {/* Icon con pulse */}
          <div className="gab-icon">
            <Inbox size={16} weight="duotone" color="#FFFFFF" />
            <span aria-hidden className="gab-icon-pulse" />
          </div>

          <div className="gab-text">
            <div className="gab-title">
              {/* Copy varía según haya pendientes (con comprobante) o solo
                  prospectos (sin comprobante todavía). El operador puede
                  actuar inmediatamente sobre los pendientes; los prospectos
                  son solo informativos hasta que suban comprobante. */}
              {pendienteCount > 0
                ? pendienteCount === 1
                  ? '¡Un asociado envió su comprobante!'
                  : `¡${pendienteCount} asociados enviaron sus comprobantes!`
                : prospectoCount === 1
                  ? '¡Un asociado arrancó su inscripción!'
                  : `¡${prospectoCount} asociados arrancaron su inscripción!`}
            </div>
            <div className="gab-sub">
              {pendienteCount > 0
                ? 'Revisá el aporte y registrá el pago para activarlos.'
                : 'Esperando que suban su comprobante de aporte.'}
            </div>
          </div>

          {/* Botón "Probar sonido" — útil para validar que el chime funciona
              sin tener que esperar a un INSERT real. El click también prima
              el AudioContext si todavía no se primó. Oculto en pantallas
              chicas para no quitarle espacio al título. */}
          <button
            type="button"
            onClick={onTestChime}
            className="gab-btn gab-btn-secondary"
            title="Probar el sonido del chime"
          >
            <SpeakerHigh size={13} weight="bold" />
            <span className="gab-btn-label">Probar</span>
          </button>

          {/* Botón opt-in para notificaciones nativas */}
          {showActivate ? (
            <button
              type="button"
              onClick={onActivateNotifications}
              className="gab-btn gab-btn-secondary"
              title="Activar notificaciones del navegador"
            >
              <BellRinging size={13} weight="bold" />
              <span className="gab-btn-label">Avisarme</span>
            </button>
          ) : null}

          <div className="gab-cta">
            <span className="gab-cta-label">Ver lista</span>
            <ArrowRight size={14} weight="bold" />
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDismiss()
            }}
            aria-label="Ocultar por 5 minutos"
            title="Ocultar por 5 minutos"
            className="gab-close"
          >
            <X size={13} weight="bold" />
          </button>
        </Link>
      </div>
    </>
  )
}

const bannerStyles = `
@keyframes neocoopPulseRing {
  0%   { transform: scale(0.85); opacity: 0.85; }
  70%  { transform: scale(1.30); opacity: 0;    }
  100% { transform: scale(1.30); opacity: 0;    }
}
.gab-wrap {
  position: sticky;
  top: 0;
  z-index: 50;
  background: #FFF6F1;                       /* crema suave, no bloque saturado */
  color: #3f2a20;                            /* texto oscuro cálido, legible */
  border-bottom: 1px solid rgba(240,78,35,0.14);  /* divisor fino, no franja */
  box-shadow: none;
}
.gab-link {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 20px;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}
.gab-icon {
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background: #F04E23;                       /* naranja SOLO acá (acento) */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.gab-icon-pulse {
  position: absolute;
  inset: -4px;
  border-radius: 20px;
  border: 2px solid rgba(240,78,35,0.35);
  animation: neocoopPulseRing 1.6s ease-out infinite;
  pointer-events: none;
}
.gab-text { flex: 1; min-width: 0; }
.gab-title { font-size: 13.5px; font-weight: 700; letter-spacing: 0.1px; color: #2c1c14; }
.gab-sub   { font-size: 11px; margin-top: 1px; letter-spacing: 0.1px; color: #8a6f63; }
.gab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #FFFFFF;
  border: 1px solid rgba(240,78,35,0.28);
  color: #C43C00;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1px;
  cursor: pointer;
}
.gab-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #F04E23;                            /* acento en el CTA */
  flex-shrink: 0;
}
.gab-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 13px;
  background: rgba(0,0,0,0.04);
  border: none;
  color: #8a6f63;
  cursor: pointer;
  flex-shrink: 0;
}

/* Tablet/iPad: banner más compacto, esconder botones extras */
@media (max-width: 1366px) {
  .gab-link { gap: 10px; padding: 10px 14px; }
  .gab-title { font-size: 13px; }
  .gab-sub   { font-size: 10.5px; }
  .gab-btn-secondary { display: none; }
}

/* Mobile: solo título + cerrar */
@media (max-width: 640px) {
  .gab-link { gap: 8px; padding: 9px 12px; }
  .gab-title { font-size: 12.5px; line-height: 1.25; }
  .gab-sub   { display: none; }
  .gab-cta-label { display: none; }
}
`
